/**
 * BOGO (Buy-1-Get-N-Free) discount engine — collection-aware.
 *
 * Rules (each collection is calculated INDEPENDENTLY):
 *   • B1G1 collection  →  every 2 units, the CHEAPEST 1 is free
 *                          (2 pcs → 1 free, 3 pcs → 1 free, 4 pcs → 2 free)
 *   • B1G2 collection  →  every 3 units, the CHEAPEST 2 are free
 *                          (3 pcs → 2 free, 4 pcs → 2 free, 5 pcs → 2 free, 6 pcs → 4 free)
 *   • Non-BOGO items   →  no discount, no effect on either tier
 *
 * The two collections DO NOT mix — a B1G1 item never unlocks B1G2 and vice-versa.
 *
 * The engine returns:
 *   • tier              currently-headlined active tier ("b1g1" | "b1g2" | null)
 *   • freeUnits         total free units across both tiers
 *   • discount          total ₹ saved
 *   • nextTier          the tier we're nudging progress toward (headline group)
 *   • unitsToNext       units needed to unlock next freebie inside the headline group
 *   • progressPct       0-100 within the headline group's next pair/triplet
 *   • freeUnitIdCounts  { [productId]: N } — for the FREE ×N pill on product cards
 */

/** Detect which BOGO tier a product belongs to based on its Shopify tags */
export function deriveBogoTier(product) {
  if (!product) return null;
  // Explicit override from collection page (most reliable)
  if (product.bogoTier === "b1g1" || product.bogoTier === "b1g2") {
    return product.bogoTier;
  }
  const raw = [
    ...(product.tags || []),
    product.category || "",
    product.collection || "",
  ]
    .map((t) => String(t).toLowerCase())
    .join(" ");
  if (/buy-?1-?get-?2|b1g2|glam-?box/.test(raw)) return "b1g2";
  if (/buy-?1-?get-?1|b1g1|mini-?box|chunky-?cuff/.test(raw)) return "b1g1";
  return null;
}

function discountFromGroup(units, freeCount) {
  if (freeCount <= 0) return 0;
  const asc = [...units].sort((a, b) => a.price - b.price);
  return asc.slice(0, freeCount).reduce((s, u) => s + u.price, 0);
}

export function computeBogo(cart) {
  // Expand each cart line into individual units carrying tier + price
  const all = [];
  cart.forEach((line) => {
    for (let i = 0; i < (line.qty || 0); i++) {
      all.push({
        id: line.id,
        price: line.price || 0,
        tier: line.bogoTier || null,
      });
    }
  });

  const g1 = all.filter((u) => u.tier === "b1g1");
  const g2 = all.filter((u) => u.tier === "b1g2");

  // Per-group free units — collections isolated
  const g1Free = Math.floor(g1.length / 2);          // buy 2 get 1
  const g2Free = Math.floor(g2.length / 3) * 2;      // buy 3 get 2

  const g1Disc = discountFromGroup(g1, g1Free);
  const g2Disc = discountFromGroup(g2, g2Free);

  const discount = g1Disc + g2Disc;
  const freeUnits = g1Free + g2Free;

  // Headline group for the celebration box:
  // Prefer B1G2 if it has items; else B1G1; else no BOGO items yet.
  let headline = null;
  if (g2.length > 0 && g2.length >= g1.length) headline = "b1g2";
  else if (g1.length > 0) headline = "b1g1";

  // What tier is currently *unlocked* (drives label text like "Offer locked in")
  let tier = null;
  if (headline === "b1g2" && g2Free > 0) tier = "b1g2";
  else if (headline === "b1g1" && g1Free > 0) tier = "b1g1";

  // Progress toward the next freebie within the headline group only
  let nextTier = null;
  let unitsToNext = 0;
  let progressPct = 0;

  if (headline === "b1g1") {
    const count = g1.length;
    const cyclePos = count % 2;                // 0 or 1
    unitsToNext = 2 - cyclePos;                // 1 or 2 more needed
    if (cyclePos === 0 && count > 0) unitsToNext = 2; // just unlocked a freebie
    nextTier = "b1g1";
    progressPct = (cyclePos / 2) * 100;
  } else if (headline === "b1g2") {
    const count = g2.length;
    const cyclePos = count % 3;                // 0, 1, 2
    unitsToNext = 3 - cyclePos;
    if (cyclePos === 0 && count > 0) unitsToNext = 3;
    nextTier = "b1g2";
    progressPct = (cyclePos / 3) * 100;
  } else {
    // No BOGO items yet — nudge them toward B1G1 as the entry offer
    nextTier = "b1g1";
    unitsToNext = 2;
    progressPct = 0;
  }

  // Which product ids get a "FREE ×N" pill (cheapest freebies picked)
  const freeUnitIdCounts = {};
  const g1Asc = [...g1].sort((a, b) => a.price - b.price);
  g1Asc.slice(0, g1Free).forEach((u) => {
    freeUnitIdCounts[u.id] = (freeUnitIdCounts[u.id] || 0) + 1;
  });
  const g2Asc = [...g2].sort((a, b) => a.price - b.price);
  g2Asc.slice(0, g2Free).forEach((u) => {
    freeUnitIdCounts[u.id] = (freeUnitIdCounts[u.id] || 0) + 1;
  });

  // Per-tier data for rendering multiple boxes side-by-side or stacked
  const g1CyclePos = g1.length % 2;
  const g1UnitsToNext = g1.length === 0 ? 2 : (g1CyclePos === 0 ? 2 : 2 - g1CyclePos);
  const g1Progress = g1.length === 0 ? 0 : (g1CyclePos / 2) * 100;
  const g1Data = g1.length > 0 ? {
    tier: "b1g1",
    freeUnits: g1Free,
    discount: g1Disc,
    unitsToNext: g1UnitsToNext,
    progressPct: g1Free > 0 && g1CyclePos === 0 ? 100 : g1Progress,
    unlocked: g1Free > 0,
    count: g1.length,
  } : null;

  const g2CyclePos = g2.length % 3;
  const g2UnitsToNext = g2.length === 0 ? 3 : (g2CyclePos === 0 ? 3 : 3 - g2CyclePos);
  const g2Progress = g2.length === 0 ? 0 : (g2CyclePos / 3) * 100;
  const g2Data = g2.length > 0 ? {
    tier: "b1g2",
    freeUnits: g2Free,
    discount: g2Disc,
    unitsToNext: g2UnitsToNext,
    progressPct: g2Free > 0 && g2CyclePos === 0 ? 100 : g2Progress,
    unlocked: g2Free > 0,
    count: g2.length,
  } : null;

  return {
    tier,
    freeUnits,
    discount,
    nextTier,
    unitsToNext,
    progressPct,
    totalUnits: all.length,
    freeUnitIdCounts,
    // Debug / secondary info (used by drawer if needed)
    groups: { b1g1: g1.length, b1g2: g2.length },
    // Per-tier boxes to render when both tiers have items
    tiers: [g1Data, g2Data].filter(Boolean),
  };
}

export const BOGO_LABELS = {
  b1g1: "Buy 1 Get 1 Free",
  b1g2: "Buy 1 Get 2 Free",
};
